import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  CheckSquare,
  Clock,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  UserCog,
  StickyNote,
  DollarSign,
  ContactRound,
} from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: null },
  { path: '/crm', label: 'CRM', icon: ContactRound, permission: 'clients' },
  { path: '/clients', label: 'Klienti', icon: Users, permission: 'clients' },
  { path: '/projects', label: 'Projekty', icon: FolderKanban, permission: 'projects' },
  { path: '/tasks', label: 'Úkoly', icon: CheckSquare, permission: 'tasks' },
  { path: '/time-tracker', label: 'Čas', icon: Clock, permission: 'time_tracking' },
  { path: '/notes', label: 'Poznámky', icon: StickyNote, permission: null },
  { path: '/revenue', label: 'Tržby', icon: TrendingUp, adminOnly: true },
  { path: '/external-costs', label: 'Ext. náklady', icon: DollarSign, adminOnly: true },
  { path: '/users', label: 'Uživatelé', icon: Settings, permission: 'users' },
];

export const Layout = ({ children }) => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => {
    // Admin-only items
    if (item.adminOnly) {
      return user?.is_admin;
    }
    // Permission-based items
    return item.permission === null || hasPermission(item.permission);
  });

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b-2 border-black z-50 flex items-center px-4">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 hover:bg-gray-100"
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="ml-4 font-oswald text-xl font-bold uppercase tracking-tight">
          WeLead
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 h-screen w-64 bg-white border-r-2 border-black z-40 transition-transform lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-20 flex items-center px-6 border-b-2 border-black bg-[#F4E600]">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="font-oswald text-2xl font-bold uppercase tracking-tight text-black">
              WeLead
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.path.slice(1)}`}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 font-oswald uppercase text-sm tracking-wide transition-all",
                  isActive
                    ? "bg-[#F4E600] text-black border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                    : "text-gray-700 hover:bg-gray-100 border-2 border-transparent"
                )}
              >
                <Icon size={18} />
                {item.label}
                {isActive && <ChevronRight size={16} className="ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t-2 border-black bg-gray-50">
          <div className="mb-3">
            <div className="font-oswald text-sm uppercase tracking-wide text-gray-500">Přihlášen</div>
            <div className="font-serif font-medium truncate">{user?.name}</div>
            <div className="text-xs text-gray-500 truncate">{user?.email}</div>
          </div>
          <div className="flex gap-2">
            <Link
              to="/my-account"
              className="flex-1 flex items-center justify-center gap-1 py-2 font-oswald uppercase text-xs border-2 border-black bg-white hover:bg-[#F4E600] transition-colors"
              data-testid="my-account-btn"
            >
              <UserCog size={14} />
              Můj účet
            </Link>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex-1 font-oswald uppercase text-xs border-2 border-black hover:bg-black hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut size={14} className="mr-1" />
              Odhlásit
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
